import React, { useState, useEffect, useRef } from 'react';
import { useLazyQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector } from 'react-redux';

import { DialogContent, Modal, Box } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import { makeStyles } from '@mui/styles';

import { ToastContainer, toast } from 'react-toastify';

import * as TeamAlertsConstants from '../utils/constants';
import * as Q from '../../../../../constants/graphqlQueries';
import colors from '../../../../../constants/colors';
import transactionResponse from '../../../../../constants/transactionResponse';
import ModalDetail from '../../../../ModalDetail/ModalDetail';
import TeamAlertsGrid from '../Grid/TeamAlertsGrid';

import '../../TeamAlerts.css';
import Search from '../Search/Search';

const useStyles = makeStyles(() => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    unsuscribe: {
        backgroundColor: '#F3F2F1',
        color: '#000',
        fontWeight: '700',
        marginTop: '0px', // unsubscribe button with no vertical margin
        marginBottom: '0px',
        padding: '10px',
        '&:hover': {
            color: colors.white,
            backgroundColor: colors.blue,
        }
    }
}));
const gridTitle = 'Subscriptions';

const TeamAlertsSubscription = () => {
    const classes = useStyles();
    const toastId = useRef(null);

    const employeeId = useSelector((state) => state.User.EmployeeId);
    const [selectedSearch, setSelectedSearch] = useState({ client: null, program: null, project: null, site: null, supervisorEmployeeId: null });
    const [open, setOpen] = useState(false);
    const [subscriptions, setSubscriptions] = useState([]);
    const [selectedSubscription, setSelectedSubscription] = useState({});

    // use mutation
    const [addSubscriptionByCPPS, { data: dataAddSubscriptionByCPPS, error: errorAddSubscriptionByCPPS }] = useMutation(
        Q.APP_ALERT_SUBSCRIBE_BY_CPPS_MUTATION,
    );
    const [addSubscriptionBySupervisor, { data: dataAddSubscriptionBySupervisor, error: errorAddSubscriptionBySupervisor }] = useMutation(
        Q.APP_ALERT_SUBSCRIBE_BY_SUPERVISOR_MUTATION,
    );
    const [deleteSubscription, { data: dataDeleteSubscription, error: errorDeleteSubscriptionByCPPS }] = useMutation(
        Q.APP_ALERT_UNSUSCRIBE_MUTATION,
    );
    // lazy queries
    const [getSubscriptions, {
        data: dataSubscriptions,
        loading: loadingSubscriptions,
        networkStatus: getSubscriptionsNetworkStatus
    }] = useLazyQuery(
        Q.APP_ALERT_SUBSCRIPTIONS_GB_QUERY,
        { notifyOnNetworkStatusChange: true }
    );
    const clearAutoRefresh = () => {
        toast.dismiss(toastId.current);
        toastId.current = null;
    }
    const onSubscribeClick = (client, program, project, site, agentEmployeeId, agentEmployeeName, supervisorEmployeeId) => {
        clearAutoRefresh();
        setSelectedSearch({ client: client ?? null, program: program ?? null, project: project ?? null, site: site ?? null, supervisorEmployeeId });
    }
    const handleOpenUnsubscribe = (event, rowData) => {
        setSelectedSubscription({ id: rowData.SubscriptionId });
        setOpen(true);
    }
    const onUnsubscribeClick = async () => {
        setOpen(false);
        await deleteSubscription({
            variables: {
            SubscriptionId: selectedSubscription.id,
            EmployeeId: employeeId.toString(),
            }
        });
    }
    const handleClose = () => {
        setOpen(false);
    }
    const onClearClick = () => {
        clearAutoRefresh();
        setSelectedSearch({ client: null, program: null, project: null, site: null, supervisorEmployeeId: null });
    }
    const gridConfigurations = {
        keyGrid: 1,
        columns: [
            { title: 'Team Lead', field: 'SupervisorName', render: (rowData) => rowData.SupervisorName ?? 'N/A' },
            { title: 'Team Lead ID', field: 'SupervisorEmployeeId', render: (rowData) => rowData.SupervisorEmployeeId ?? 'N/A' },
            { title: 'Client', field: 'ClientName', render: (rowData) => rowData.ClientName ?? 'N/A' },
            { title: 'Program', field: 'ProgramName', render: (rowData) => rowData.ProgramName ?? 'N/A' },
            { title: 'Project', field: 'ProjectName', render: (rowData) => rowData.ProjectName ?? 'N/A' },
            { title: 'Site', field: 'SiteName', render: (rowData) => rowData.SiteName ?? 'N/A' }
            ],
        options: {
            // TODO: deactivated because we are loosing the grouping in every request to the server.
            // grouping: true,
            defaultExpanded: true,
            filtering: false,
            actionsColumnIndex: 0,
            tableLayout: 'auto',
            actionsCellStyle: {
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
            },
        },
        localization: {
            header: {
                actions: 'Actions',
            },
            body: {
                emptyDataSourceMessage: 'Search for alerts you would like to subscribe to',
            },
        },
        actions: [{
            // eslint-disable-next-line react/display-name
            icon: () => <p className={classes.unsuscribe}>Unsubscribe</p>,
            onClick: (event, rowData) => handleOpenUnsubscribe(event, rowData),
        }]
    }
    const [gridConfiguration] = useState(gridConfigurations);

    useEffect(() => {
        getSubscriptions({
            variables: {
                EmployeeId: employeeId.toString(),
            },
        });
    }, [employeeId]);

    useEffect(() => {
        if (dataDeleteSubscription) {
            clearAutoRefresh();
            toastId.current = toast(transactionResponse(null, dataDeleteSubscription.unSubscribeToOtherTeam.Status, TeamAlertsConstants.transactionMessages.unsubcription));
            getSubscriptions({
            variables: {
                EmployeeId: employeeId.toString(),
            }
            });
        }

        if (errorDeleteSubscriptionByCPPS) {
            toast(TeamAlertsConstants.transactionMessages.unsubcribeFailed);
        }
    }, [dataDeleteSubscription, errorDeleteSubscriptionByCPPS]);

    useEffect(() => {
        if (dataAddSubscriptionByCPPS) {
            if (dataAddSubscriptionByCPPS.subscribeToOtherTeamByCPPS && !errorAddSubscriptionByCPPS) {
            clearAutoRefresh();
            toastId.current = toast(transactionResponse(null, dataAddSubscriptionByCPPS.subscribeToOtherTeamByCPPS.Status, TeamAlertsConstants.transactionMessages.subcription));

            getSubscriptions({
                variables: {
                EmployeeId: employeeId.toString()
                }
            });
            }
        }
    }, [dataAddSubscriptionByCPPS]);

    useEffect(() => {
        if (dataAddSubscriptionBySupervisor) {
            if (dataAddSubscriptionBySupervisor.subscribeToOtherTeamBySupervisor && !errorAddSubscriptionBySupervisor) {
                clearAutoRefresh();
                toastId.current = toast(transactionResponse(null, dataAddSubscriptionBySupervisor.subscribeToOtherTeamBySupervisor.Status, TeamAlertsConstants.transactionMessages.subcription));

                getSubscriptions({
                    variables: {
                        EmployeeId: employeeId.toString()
                    }
                });
            }
        }
    }, [dataAddSubscriptionBySupervisor]);

    useEffect(() => {
        if (errorAddSubscriptionByCPPS) {
            toast(TeamAlertsConstants.transactionMessages.subcribeFailed);
        }
    }, [errorAddSubscriptionByCPPS]);

    useEffect(() => {
        if (errorAddSubscriptionBySupervisor) {
            toast(TeamAlertsConstants.transactionMessages.subcribeFailed);
        }
    }, [errorAddSubscriptionBySupervisor]);

    useEffect(() => {
        if (dataSubscriptions && getSubscriptionsNetworkStatus === NetworkStatus.ready) {
            setSubscriptions(dataSubscriptions.getActiveSubscriptions);
        }
    }, [dataSubscriptions, getSubscriptionsNetworkStatus]);

    useEffect(() => {
        if (selectedSearch.supervisorEmployeeId) {
            addSubscriptionBySupervisor({
                variables: {
                    EmployeeId: employeeId.toString(),
                    SupervisorEmployeeId: selectedSearch.supervisorEmployeeId
                }
            });
        }
        else if (selectedSearch.client && selectedSearch.program && selectedSearch.project && selectedSearch.site) {
            addSubscriptionByCPPS({
                variables: {
                    ClientCode: selectedSearch.client.ClientId,
                    ProgramCode: selectedSearch.program.ProgramId,
                    ProjectCode: selectedSearch.project.ProjectId,
                    SiteCode: selectedSearch.site.SiteId,
                    EmployeeId: employeeId.toString(),
                }
            });
        }
    }, [selectedSearch]);

    return <Box className='__teamAlerts'>
        <div className='__teamAlerts__filters'>
            <Search 
                buttonName='Subscribe'
                onSearchClick={onSubscribeClick} 
                onClear={onClearClick} 
                tooltipSearchMessage={selectedSearch.supervisorEmployeeId ? TeamAlertsConstants.tooltipMessages.subscribeTL : TeamAlertsConstants.tooltipMessages.subscribeCPPS}
            />
        </div>
        <div className='__teamAlerts__grid'>
            <Box className='__teamAlerts__grid--container'>
                <Box className='__teamAlerts__grid--container-title'>
                <span>
                    {gridTitle}
                </span>
                </Box>
            </Box>
            <TeamAlertsGrid
                keyGrid={gridConfiguration.keyGrid}
                columns={gridConfiguration.columns}
                actions={gridConfiguration.actions}
                options={gridConfiguration.options}
                localization={gridConfiguration.localization}
                data={subscriptions.map((o) => ({ ...o }))}
                isLoading={loadingSubscriptions}
            />
        </div>

        <ToastContainer hideProgressBar />

        <Modal
            className={classes.modal}
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
            timeout: 5000,
            }}
        >
            <DialogContent className='dialog delete-rule'>
            <ModalDetail className='modal delete-rule'
                modalOptions={TeamAlertsConstants.modalOptions}>
                <button className='simple-modal-btn' type='button' onClick={onUnsubscribeClick}>Unsubscribe</button>
                <button className='simple-modal-btn' type='button' onClick={handleClose}>Cancel</button>
                </ModalDetail>
            </DialogContent>
        </Modal>
    </Box>
}

export default TeamAlertsSubscription